import { anthropicStream } from './anthropicProxy';

// ============================================================
// MANDATORY DESIGN FOOTER (appended to every generated prompt)
// ============================================================
const DESIGN_FOOTER = `

---

## Mandatory Design & Technology Requirements

**All UI components and design elements in this application MUST follow these requirements:**

- **Component Library**: Use **shadcn/ui** for every UI primitive and component (buttons, inputs, modals, cards, selects, dialogs, tabs, dropdowns, tables, forms, etc.). Do NOT use raw HTML elements or other component libraries.
- **Styling Framework**: Use **Tailwind CSS v4** for all styling. All custom styles must be written as Tailwind utility classes. Do not use plain CSS, CSS modules, or styled-components.
- **Typography**: Use the **Inter** font family as the primary typeface across the entire application. Set it as the default sans-serif font in the Tailwind config. Use font weights: 400 (regular body text), 500 (medium for labels/subheadings), 600 (semibold for headings), and 700 (bold for emphasis/titles).
- **Design System Consistency**: Maintain consistent spacing (use Tailwind spacing scale), border-radius (use rounded-lg as default), and color tokens from shadcn/ui theme. Ensure dark mode support via shadcn/ui theming.
- **Responsive Design**: All pages and components must be fully responsive using Tailwind breakpoints (sm, md, lg, xl).
- **Accessibility**: Follow shadcn/ui's built-in accessibility patterns. All interactive elements must be keyboard-navigable and screen-reader friendly.
- **No Gradients**: Use flat, solid colors only. No gradient backgrounds, gradient text, or gradient buttons.
- **No Emojis**: Do not use emojis anywhere in the UI. Use Lucide React icons sparingly for functional purposes only.

> **This section is non-negotiable and must be applied to the entire application regardless of other design preferences mentioned above.**
`;

// ============================================================
// SYSTEM PROMPT — Compact, high-signal version for speed
// ============================================================
const SYSTEM_PROMPT = `<role>Senior Product Manager creating one-shot build prompts for Claude Code.</role>

<context>Transform a user's app idea into a product-focused prompt that Claude Code executes to build the MVP in one session. Focus on WHAT to build and HOW users experience it — not heavy technical details. Claude Code handles the technical implementation itself. The mandatory tech stack (Next.js, Tailwind v4, shadcn/ui) is appended to your output automatically.</context>

<output_structure>
Generate a Markdown document with exactly these 4 sections. Be concise — no filler.

### Section 1: App Overview
App Name, Summary (3-4 sentences: what it does, the problem it solves, who it's for), Target Audience (2-3 bullets), Primary Use Cases (3-5 bullets).

### Section 2: Functionalities
For each feature: Name, Purpose (one sentence on user value), How It Works (3-5 numbered user-facing steps including what happens on errors), Acceptance Criteria (3-4 testable bullets from user's perspective).
End with Non-Goals (2-3 bullets — things explicitly out of scope).

### Section 3: User Flow Breakdown
For each journey: Flow Name, Steps (5-8 numbered from user's perspective — what they see and do), Error Paths (1-2 bullets).

### Section 4: Page-by-Page Breakdown
For each page/route: Route path, Purpose (one sentence), Layout description (what's on the page visually), Key Actions (what users can do here).
</output_structure>

<design_rules>
- Flat solid colors only. NO gradients.
- NO emojis in the UI.
- Clean, professional, minimal.
</design_rules>

<quality_rules>
- Only include features tied to the user's idea. No extras.
- No placeholder text. No code. No database schemas. No file structures. No terminal commands.
- Concise bullets (≤14 words). Strong verbs. No hedging.
- Specific language: "A grid of cards showing name, date, status" not "display items."
- Keep it product-focused. Skip implementation details — Claude Code will handle those.
</quality_rules>

<final_line>After the document, append verbatim: _When you finish building, tell me the next steps to continue this app according to the prompt and what you'll work on next. Ask me whether to continue with those or modify the plan._</final_line>`;


// ============================================================
// PUBLIC API — Streaming Opus 4.7 generation
// ============================================================
//
// Speed levers applied (per claude-api skill best practices for Opus 4.7):
//   1. Streaming           — tokens appear live; perceived latency drops from ~60s to ~2-3s
//   2. effort: "low"       — Opus 4.7 defaults to "high"; lowering is the biggest actual speed win
//   3. thinking: disabled  — PRD generation is a structured writing task, doesn't need reasoning
//
// Note on prompt caching: not enabled here because Opus 4.7's minimum cacheable prefix
// is 4096 tokens, and our system prompt is ~500 tokens. It would silently not cache.
//
export const generateClaudeCodePrompt = async (appIdea, onProgress) => {
  try {
    // Buffer the streaming output and emit the current accumulated text + footer
    let accumulated = '';

    const fullText = await anthropicStream(
      {
        model: 'claude-opus-4-7',
        max_tokens: 4096,
        system: SYSTEM_PROMPT,
        messages: [{
          role: 'user',
          content: `Create a one-shot Claude Code prompt for this app idea:\n\n${appIdea}`
        }],
        thinking: { type: 'disabled' },
        output_config: { effort: 'low' },
      },
      {
        onText: (delta) => {
          accumulated += delta;
          // Emit partial text so the UI can render it live. Don't append the footer yet —
          // wait until generation completes so the footer appears at the end.
          if (onProgress) onProgress(accumulated);
        },
      }
    );

    const finalText = (fullText || '') + DESIGN_FOOTER;
    if (onProgress && finalText) onProgress(finalText);
    return finalText;
  } catch (error) {
    console.error('Error generating Claude Code prompt:', error);
    throw error;
  }
};
